<template>
  <AppSkipToMainContent />
	<div :class="layoutContainerClass" @click="onDocumentClick">
    <AppTopBar :horizontal="appState.user.preferences.appMenuMode==='horizontal'" :topbarMenuActive="topbarMenuActive" :activeTopbarItem="activeTopbarItem" :mobileTopbarActive="mobileTopbarActive" @topbar-mobileactive="onTopbarMobileButtonClick" @menubutton-click="onMenuButtonClick" @topbaritem-click="onTopbarItemClick" :searchActive="searchActive" :menuActive="menuActive" @search-toggle="onSearchToggle" @search-click="onSearchClick" @search-hide="onSearchHide" @settings-click="onSettingsClick"></AppTopBar>
    <div class="menu-wrapper">
      <div id="main-menu" class="layout-menu-container" tabindex="0" @click="onMenuClick">
        <AppMenuUser />
        <AppMenu
          :active="menuActive"
          :mobileMenuActive="mobileMenuActive"
          :isSlimOrHorizontalItemClick="isSlimOrHorizontalItemClick"
          data-cy="App_app-menu"
          @menuitem-click="onMenuItemClick"
          @root-menuitem-click="onRootMenuItemClick"
        />
        <AppMenuFaq />
      </div>
    </div>
		<div class="layout-main" tabindex="0">
			<div class="layout-content"><router-view :key="routerViewKey" /></div>
			<AppFooter />
		</div>
    <AppSettings />
    <AppContact />
    <div v-if="mobileMenuActive" class="layout-mask modal-in"></div>
    <Toast position="bottom-right">
      <template #message="slotProps">
        <span class="p-toast-message-icon material-symbols-outlined check" v-if="slotProps.message.severity === 'success'"></span>
        <span class="p-toast-message-icon material-symbols-outlined info" v-if="slotProps.message.severity === 'info'"></span>
        <span class="p-toast-message-icon material-symbols-outlined warning" v-if="slotProps.message.severity === 'warn'"></span>
        <span class="p-toast-message-icon material-symbols-outlined close" v-if="slotProps.message.severity === 'error'"></span>
        <div class="p-toast-message-text">
          <span class="p-toast-summary" v-html="slotProps.message.summary"></span>
          <div class="p-toast-detail" v-html="slotProps.message.detail"></div>
        </div>
      </template>
    </Toast>
    <ConfirmDialog group="dialog" class="p-dialog-skinny" data-cy="App_confirm-dialog"/>
    <ConfirmPopup group="popup" class="max-w-40rem" data-cy="App_confirm-popup" />
    <AppFileViewerDialog />
    <AppTokenExpirationDialog />
    <AppNewVersionAvailableDialog />
    <ScrollTop />
	</div>
</template>

<script setup>
  import { inject, ref, watch, computed, onMounted } from "vue";
  import { useRoute } from "vue-router";
  import AppSkipToMainContent from "@/views/_app/AppSkipToMainContent.vue";
  import AppTopBar from "@/views/_app/AppTopBar.vue";
  import AppMenuUser from "@/views/_app/AppMenuUser.vue";
  import AppMenu from "@/views/_app/AppMenu.vue";
  import AppMenuFaq from "@/views/_app/AppMenuFaq.vue";
  import AppSettings from "@/views/_app/AppSettings.vue";
  import AppContact from "@/views/_app/AppContact.vue";
  import AppFooter from "@/views/_app/AppFooter.vue";
  import AppTokenExpirationDialog from "@/views/_app/AppTokenExpirationDialog.vue";
  import AppFileViewerDialog from "@/views/_app/AppFileViewerDialog.vue";
  import AppNewVersionAvailableDialog from "@/views/_app/AppNewVersionAvailableDialog.vue";
  import EventBus from "@/utilities/eventBus.js";
  import getCssVariable from "@/utilities/getCssVariable.js";
  import { useToastUtil } from "@/utilities/useToastUtil.js";

  const appState = inject("appState");
  const route = useRoute();
  const toastUtil = useToastUtil();
  const routerViewKey = ref(0);
  const mobileTopbarActive = ref(false);
  const mobileMenuActive = ref(false);
  const searchClick = ref(false);
  const searchActive = ref(false);
  const settingsClick = ref(false);
  const overlayMenuActive = ref(false);
  const topbarMenuActive = ref(false);
  const activeTopbarItem = ref();
  const isSlimOrHorizontalItemClick = ref(false);
  const menuActive = ref(true);
  const topbarItemClick = ref(false);
  const menuClick = ref(false);
  const isSlim = () => appState.user.preferences.appMenuMode === "slim";
  const isOverlay = () => appState.user.preferences.appMenuMode === "overlay";
  const isStatic = () => appState.user.preferences.appMenuMode === "static";
  const isHorizontal = () => appState.user.preferences.appMenuMode === "horizontal";
  const isMobile = () => window.innerWidth <= 991;
  function onDocumentClick() {
    if (!searchClick.value && searchActive.value) onSearchHide();
    if(!topbarItemClick.value) {
      activeTopbarItem.value = null;
      topbarMenuActive.value = false;
      appState.isAppSettingsPanelVisible = false;
      if(appState.zendeskWidget.isVisible) zE("webWidget", "close");
    }
    if(!menuClick.value) {
      if(isHorizontal() || isSlim()) {
        menuActive.value = false;
        isSlimOrHorizontalItemClick.value = false;
        EventBus.emit("reset-active-index");
      }
      if (isOverlay()) menuActive.value = false;
      hideOverlayMenu();
    }
    topbarItemClick.value = false;
    menuClick.value = false;
    searchClick.value = false;
  }
  function onSearchToggle() {
    searchActive.value = !searchActive.value;
    searchClick.value = true;
  }
  function onSearchClick() {
    searchClick.value = true;
  }
  function onSearchHide() {
    searchActive.value = false;
    searchClick.value = false;
  }
  function onSettingsClick() {
    settingsClick.value = true;
    appState.isAppSettingsPanelVisible = !appState.isAppSettingsPanelVisible;
  }
  function hideOverlayMenu() {
    overlayMenuActive.value = false;
    mobileMenuActive.value = false;
  }
  function onMenuButtonClick(event) {
    event.preventDefault();
    menuClick.value = true;
    menuActive.value = !menuActive.value;
    topbarMenuActive.value = false;
    if(isMobile()) mobileMenuActive.value = !mobileMenuActive.value;
  }
  function onTopbarItemClick(event) {
    event.originalEvent.preventDefault();
    topbarItemClick.value = true;
    activeTopbarItem.value = (activeTopbarItem.value === event.item ? null : event.item);
    if (event.item !== "support" && appState.zendeskWidget.isVisible) zE("webWidget", "close");
    if (event.item !== "settings") appState.isAppSettingsPanelVisible = false;
  }
  function onTopbarMobileButtonClick(event) {
    event.preventDefault();
    mobileTopbarActive.value = !mobileTopbarActive.value;
  }
  function onMenuClick() {
    menuClick.value = true;
  }
  function onRootMenuItemClick(event) {
    isSlimOrHorizontalItemClick.value = !event.isSameIndex;
    menuActive.value = !menuActive.value;
  }
  function onMenuItemClick(event) {
    if(!event.item.items) {
      isSlimOrHorizontalItemClick.value = false;
      hideOverlayMenu();
      EventBus.emit("reset-active-index");
      if(isHorizontal() || isSlim()) menuActive.value = false;
    }
  }
  watch(() => route, () => {
    menuActive.value = isStatic() && isMobile();
    toastUtil.clearAll();
  });
  watch(() => route.path, () => document.body.focus());      
  const layoutContainerClass = computed(() => {
    const htmlElement = document.documentElement;
    for (let i = htmlElement.classList.length - 1; i >= 0; i--) {
      const className = htmlElement.classList[i];
      if (className.startsWith("layout-")) htmlElement.classList.remove(className);
    }
    // add proper layout-* classes to the body tag
    const htmlClasses = [ "layout-menu-" + appState.user.preferences.appMenuMode ];
    if(appState.user.preferences.appLayoutMode === "dark") htmlClasses.push("layout-mode-dark");
    if(menuActive.value) htmlClasses.push("layout-menu-active");
    if(overlayMenuActive.value) htmlClasses.push("layout-menu-overlay-active");
    if(mobileMenuActive.value) htmlClasses.push("layout-menu-mobile-active");
    if(mobileTopbarActive.value) htmlClasses.push("layout-topbar-mobile-active");
    htmlElement.className = htmlClasses.join(" ");
    return "layout-wrapper";
  });
  watch(() => appState.user.preferences.appMenuMode, (newVal) => {
    overlayMenuActive.value = false;
    if(newVal === "static") menuActive.value = true;
  });
  watch(() => appState.user.preferences.appLayoutMode, () => {
    const colorPrimary = getCssVariable("--color-primary");
    if (appState.zendeskWidget.isAvailable) zE("webWidget", "updateSettings", {
      webWidget: {
        color: { theme: colorPrimary, launcher: colorPrimary, launcherText: colorPrimary, button: colorPrimary, resultLists: colorPrimary, header: "#2f2f2f", articleLinks: colorPrimary },
      }
    });
  }, { immediate: true });

  onMounted(() => {
    zE("webWidget:on", "open", () => appState.zendeskWidget.isVisible = true);
    zE("webWidget:on", "close", () => appState.zendeskWidget.isVisible = false);
    EventBus.on("add-toast", ({ severity, args }) => toastUtil[severity](...args));
    EventBus.on("reload-current-route", () => routerViewKey.value++);
  });
</script>